import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Printer, Power, PowerOff, Search } from "lucide-react";
import QRCode from "qrcode";

const CAT_LABEL = {
  TABLE_DECORATION: "Best Table Decoration",
  TRADITIONAL_COSTUME: "Best Traditional Costume",
  BEST_FOOD: "Best Food",
};

export default function AdminVoting() {
  const [tab, setTab] = useState("dashboard");
  const [results, setResults] = useState(null);
  const [log, setLog] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const canvasRef = useRef(null);

  const voteUrl = `${window.location.origin}/vote`;

  const load = async () => {
    try { const r = await api.get("/voting/results"); setResults(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "ALL") params.status_filter = statusFilter;
      const r2 = await api.get("/voting/log", { params }); setLog(r2.data.items);
    } catch (e) {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, voteUrl, { width: 260, margin: 2, color: { dark: "#0B1120", light: "#ffffff" } });
    }
  }, [voteUrl, tab]);

  const toggleStatus = async () => {
    const next = results?.status === "OPEN" ? "CLOSED" : "OPEN";
    if (next === "CLOSED" && !window.confirm("Close voting? Guests will no longer be able to submit votes.")) return;
    try { await api.put("/voting/settings", { status: next }); toast.success(`Voting ${next}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadQR = () => {
    if (!canvasRef.current) return;
    const a = document.createElement("a"); a.download = "food-bazaar-voting-qr.png"; a.href = canvasRef.current.toDataURL("image/png"); a.click();
  };

  if (!results) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Contest</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">VOTING</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase border ${results.status === "OPEN" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-rose-500/20 text-rose-300 border-rose-500/40"}`} data-testid="voting-status">{results.status}</span>
          <Button onClick={toggleStatus} className="bg-slate-900 border border-slate-700 text-slate-200" data-testid="voting-toggle">
            {results.status === "OPEN" ? <><PowerOff size={16} className="mr-1" /> Close Voting</> : <><Power size={16} className="mr-1" /> Open Voting</>}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-800">
        {["dashboard", "log", "qr"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2 font-display font-bold tracking-tight uppercase text-sm ${tab === t ? "text-orange-400 border-b-2 border-orange-500" : "text-slate-400 hover:text-slate-200"}`}>{t}</button>
        ))}
      </div>

      {tab === "dashboard" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["Eligible", results.eligible_tickets, "stat-eligible"],
              ["Unique Voters", results.unique_voters, "stat-voters"],
              ["Total Votes", results.total_votes, "stat-votes"],
              ["Complete Ballots", results.complete_ballots, "stat-complete"],
              ["Partial Ballots", results.partial_ballots, "stat-partial"],
              ["Not Yet Voted", results.not_voted, "stat-not-voted"],
            ].map(([k, v, tid]) => (
              <div key={k} className="rounded-2xl bg-[#131D33] border border-slate-800 p-4" data-testid={tid}>
                <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold">{k}</div>
                <div className="font-display text-3xl font-black text-white">{v}</div>
              </div>
            ))}
          </div>

          {Object.entries(results.by_category).map(([key, r]) => (
            <div key={key} className="bg-[#131D33] border border-slate-800 rounded-2xl p-5">
              <div className="flex justify-between items-baseline mb-3">
                <h3 className="font-display text-2xl font-black tracking-tight text-white">{r.label}</h3>
                <span className="text-slate-400 text-sm">{r.total} votes</span>
              </div>
              <div className="space-y-2">
                {r.counts.map((row) => (
                  <div key={row.country} data-testid={`res-${key}-${row.country.replace(/ /g,'-')}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-200">{row.country}</span>
                      <span className="text-slate-400 font-mono-fb">{row.votes} · {row.pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-900 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500" style={{ width: `${row.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "log" && (
        <>
          <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-3 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search ticket ID or guest name…" className="pl-9 bg-[#0B1120] border-slate-700 text-white h-10" data-testid="log-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#0B1120] border border-slate-700 text-white rounded-md h-10 px-3 text-sm" data-testid="log-status-filter">
              <option value="ALL">All Statuses</option>
              <option value="COMPLETE">Complete</option>
              <option value="PARTIAL">Partial</option>
            </select>
            <Button onClick={load} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="log-search-btn">Search</Button>
          </div>

          <div className="bg-[#131D33] border border-slate-800 rounded-2xl overflow-hidden mt-3">
            <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-widest text-slate-400 border-b border-slate-800 bg-black/20">
              <div className="col-span-2">Ticket</div>
              <div className="col-span-3">Guest</div>
              <div className="col-span-2">Table</div>
              <div className="col-span-2">Costume</div>
              <div className="col-span-2">Food</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            <div className="divide-y divide-slate-800">
              {log.length === 0 && <div className="p-8 text-center text-slate-500">No votes yet.</div>}
              {log.map((row) => (
                <div key={row.ticket_id} className="grid grid-cols-2 md:grid-cols-12 px-4 py-3 items-center" data-testid={`log-${row.ticket_visible}`}>
                  <div className="md:col-span-2 font-mono-fb text-orange-300">{row.ticket_visible}</div>
                  <div className="md:col-span-3 text-slate-200 truncate">{row.guest_name || "—"}</div>
                  <div className="md:col-span-2 text-slate-300">{row.TABLE_DECORATION || <span className="text-slate-600">—</span>}</div>
                  <div className="md:col-span-2 text-slate-300">{row.TRADITIONAL_COSTUME || <span className="text-slate-600">—</span>}</div>
                  <div className="md:col-span-2 text-slate-300">{row.BEST_FOOD || <span className="text-slate-600">—</span>}</div>
                  <div className="md:col-span-1 text-right">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${row.ballot_status === "COMPLETE" ? "bg-emerald-500/20 text-emerald-300" : "bg-orange-500/20 text-orange-300"}`}>{row.ballot_status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === "qr" && (
        <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-6 max-w-lg mx-auto text-center">
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Public voting</div>
          <h3 className="font-display text-2xl font-black text-white mt-1 tracking-tight">GENERAL VOTING QR CODE</h3>
          <div className="mt-4 inline-block bg-white p-4 rounded-2xl">
            <canvas ref={canvasRef} data-testid="voting-qr" />
          </div>
          <div className="mt-3 font-mono-fb text-orange-300 break-all text-sm">{voteUrl}</div>
          <div className="mt-4 flex gap-2 justify-center">
            <Button onClick={downloadQR} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="qr-download">
              <Download size={14} className="mr-1" /> Download PNG
            </Button>
            <Button onClick={() => window.print()} className="bg-slate-900 border border-slate-700 text-slate-200">
              <Printer size={14} className="mr-1" /> Print
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
